import type {
  ChannelInput,
  ChannelOutput,
  ComposeBindPreview,
  ComposeCandidateSummary,
  ComposePlanPreview,
  RuntimeTargetSupport,
} from '../_shared/channel-types'
import { ONTOLOGY_CANDIDATE_QUALITY_FLOOR as QUALITY_FLOOR } from '../_shared/channel-types'
import type { ResolvedExample } from './mock-corpus-picker'

const DATA_TABLE_INTENT_RE = /\b(data[-\s]?table|table|grid|spreadsheet|rows?|columns?|dataset)\b/i
const TIMELINE_CANDIDATE_RE = /\b(timeline|roadmap|activity[-\s]?feed|chronology)\b/i

export function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(1, value))
}

export function unique(values: readonly string[], limit = 8): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const value of values) {
    const trimmed = value.trim()
    if (!trimmed || seen.has(trimmed)) continue
    seen.add(trimmed)
    out.push(trimmed)
    if (out.length >= limit) break
  }
  return out
}

function intentLabel(input: ChannelInput): string {
  if (input.kind === 'examplePick') return input.exampleId
  return input.pattern || input.semanticType || input.dsId || 'Untitled app plan'
}

export function lastSegment(value: string | undefined): string {
  if (!value) return 'section'
  const idx = Math.max(value.lastIndexOf('#'), value.lastIndexOf('/'))
  return (idx >= 0 ? value.slice(idx + 1) : value).trim() || 'section'
}

/**
 * Runtime-target support heuristic over a candidate's descriptive text
 * (semanticType + tags). Exported for the real-mode provenance card, which
 * only has the backend trace's semanticType string to go on.
 */
export function runtimeTargetsForText(text: string): Record<string, RuntimeTargetSupport> {
  const semanticType = text.toLowerCase()
  const mobileSupport: RuntimeTargetSupport =
    semanticType.includes('table') || semanticType.includes('dashboard') ? 'partial' : 'supported'
  const flutterSupport: RuntimeTargetSupport =
    semanticType.includes('iframe') || semanticType.includes('embed') ? 'unsupported' : 'partial'
  return {
    web: 'supported',
    mobile: mobileSupport,
    flutter: flutterSupport,
  }
}

function runtimeTargetsFor(example: ResolvedExample): Record<string, RuntimeTargetSupport> {
  return runtimeTargetsForText(`${example.semanticType ?? ''} ${example.exampleTags.join(' ')}`)
}

function collectDirectKeys(value: unknown, keys: readonly string[]): string[] {
  if (!value || typeof value !== 'object') return []
  const record = value as Record<string, unknown>
  const out: string[] = []
  for (const key of keys) {
    const nested = record[key]
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) continue
    for (const nestedKey of Object.keys(nested)) out.push(nestedKey)
  }
  return out
}

function collectExpressionRefs(value: unknown, prefixes: readonly string[]): string[] {
  const out: string[] = []
  const visit = (node: unknown) => {
    if (out.length >= 24) return
    if (typeof node === 'string') {
      const re = /#\{([^}]+)\}/g
      let match: RegExpExecArray | null
      while ((match = re.exec(node)) !== null) {
        const ref = match[1]?.trim()
        if (ref && prefixes.some((prefix) => ref.startsWith(prefix))) out.push(ref)
      }
      return
    }
    if (!node || typeof node !== 'object') return
    if (Array.isArray(node)) {
      for (const item of node) visit(item)
      return
    }
    for (const child of Object.values(node as Record<string, unknown>)) visit(child)
  }
  visit(value)
  return out
}

function collectActionNames(value: unknown): string[] {
  const out: string[] = []
  const visit = (node: unknown) => {
    if (out.length >= 24 || !node || typeof node !== 'object') return
    if (Array.isArray(node)) {
      for (const item of node) visit(item)
      return
    }
    const record = node as Record<string, unknown>
    for (const key of ['action', 'actionType', 'type', 'flowId']) {
      const candidate = record[key]
      if (typeof candidate === 'string' && candidate.trim()) out.push(candidate)
    }
    for (const child of Object.values(record)) visit(child)
  }
  visit(value)
  return out
}

/**
 * Summarize the state/action/datasource surface of any block-shaped JSON.
 * Exported for the real-mode provenance card, which summarizes the ACTUAL
 * returned bundle instead of a mock-corpus example.
 */
export function summarizeBlockBindings(block: unknown): Pick<
  ComposeCandidateSummary,
  'requiredState' | 'requiredActions' | 'requiredDatasources'
> {
  const requiredState = unique([
    ...collectDirectKeys(block, ['state', 'variables', 'localState']),
    ...collectExpressionRefs(block, ['local.', 'props.', 'appData.', 'computed.']),
  ])
  const requiredDatasources = unique([
    ...collectDirectKeys(block, ['data', 'datasource', 'datasources']),
    ...collectExpressionRefs(block, ['data.', 'datasource.']),
  ])
  const requiredActions = unique([
    ...collectDirectKeys(block, ['events', 'flow', 'flows', 'actions']),
    ...collectActionNames(block),
  ])
  return { requiredState, requiredActions, requiredDatasources }
}

function summarizeExampleBindings(example: ResolvedExample): Pick<
  ComposeCandidateSummary,
  'requiredState' | 'requiredActions' | 'requiredDatasources'
> {
  return summarizeBlockBindings(example.boundBlock as unknown)
}

function blockReasonFor(input: ChannelInput, example: ResolvedExample): string | undefined {
  if (example.qualityGrade === undefined) {
    return `Missing rendered design-quality grade; floor=${QUALITY_FLOOR}.`
  }
  if (example.qualityGrade < QUALITY_FLOOR) {
    return `Rendered design-quality grade below floor (${example.qualityGrade} < ${QUALITY_FLOOR}).`
  }
  const intent = intentLabel(input)
  const candidateText = `${example.semanticType ?? ''} ${example.exampleTags.join(' ')} ${example.pageName}`
  if (DATA_TABLE_INTENT_RE.test(intent) && TIMELINE_CANDIDATE_RE.test(candidateText)) {
    return 'Table intent matched a timeline pattern; requires explicit approval before binding.'
  }
  return undefined
}

function candidateScore(example: ResolvedExample, index: number): number {
  const sourceScore = example.score ?? example.intentMatch ?? 0.82 - index * 0.035
  return clamp01(sourceScore)
}

function buildCandidate(
  input: ChannelInput,
  example: ResolvedExample,
  index: number,
): ComposeCandidateSummary {
  const bindings = summarizeExampleBindings(example)
  const score = candidateScore(example, index)
  const intentMatch = clamp01(example.intentMatch ?? score + 0.04)
  const qualityGrade = example.qualityGrade
  const blockReason = blockReasonFor(input, example)
  const evidenceState =
    blockReason ? 'missing' :
    example.bindWarnings.length > 0 ? 'partial' :
    'complete'

  return {
    id: example.id,
    semanticType: lastSegment(example.semanticType ?? example.exampleTags[0]),
    patternRef: example.patternRef ?? example.id,
    intentMatch,
    ...(qualityGrade !== undefined ? { qualityGrade } : {}),
    score,
    runtimeTargets: runtimeTargetsFor(example),
    ...bindings,
    evidenceState,
    status: blockReason ? 'blocked' : 'ready',
    ...(blockReason ? { blockReason } : {}),
  }
}

function buildBindPreview(candidates: readonly ComposeCandidateSummary[], warnings: readonly string[]): ComposeBindPreview {
  const readyCandidates = candidates.filter((candidate) => candidate.status === 'ready')
  return {
    state: unique(readyCandidates.flatMap((candidate) => candidate.requiredState)),
    actions: unique(readyCandidates.flatMap((candidate) => candidate.requiredActions)),
    datasources: unique(readyCandidates.flatMap((candidate) => candidate.requiredDatasources)),
    warnings: unique(warnings, 5),
  }
}

function buildPlan(
  input: ChannelInput,
  candidates: readonly ComposeCandidateSummary[],
  bindPreview: ComposeBindPreview,
): ComposePlanPreview {
  return {
    intent: intentLabel(input),
    pageCount: candidates.filter((candidate) => candidate.status === 'ready').length,
    candidateCount: candidates.length,
    readyCandidateCount: candidates.filter((candidate) => candidate.status === 'ready').length,
    blockedCandidateCount: candidates.filter((candidate) => candidate.status === 'blocked').length,
    stateBindingCount: bindPreview.state.length,
    actionBindingCount: bindPreview.actions.length,
    datasourceBindingCount: bindPreview.datasources.length,
  }
}

export function buildComposePreviewOutput(
  input: ChannelInput,
  examples: readonly ResolvedExample[],
): Extract<ChannelOutput, { kind: 'compose-preview' }> {
  const candidates = examples
    .map((example, index) => buildCandidate(input, example, index))
    .sort((a, b) => {
      if (a.status !== b.status) return a.status === 'ready' ? -1 : 1
      return b.score - a.score
    })
  const bindPreview = buildBindPreview(candidates, examples.flatMap((example) => example.bindWarnings))
  const plan = buildPlan(input, candidates, bindPreview)
  return {
    kind: 'compose-preview',
    plan,
    candidates,
    bindPreview,
  }
}
